import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pprApi,
  PPR_POSITIONS, type PprPosition, type PprReviewType,
} from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { defaultReviewType } from './pprShared';

const css = `
  .ppr-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: var(--ew-s-6) var(--ew-s-4);
    overflow-y: auto;
  }
  .ppr-modal {
    background: #fff;
    border-radius: var(--ew-r-lg);
    max-width: 880px;
    width: 100%;
    box-shadow: var(--ew-shadow-3);
    overflow: hidden;
  }
  .ppr-modal-header {
    padding: var(--ew-s-4) var(--ew-s-5);
    background: var(--ew-dark);
    color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .ppr-modal-header h2 {
    font: var(--ew-fw-semibold) var(--ew-fs-h5)/1 var(--ew-font);
  }
  .ppr-modal-close {
    background: transparent;
    border: 0;
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    padding: 0 6px;
  }
  .ppr-modal-body {
    padding: var(--ew-s-5);
    max-height: 70vh;
    overflow-y: auto;
  }
  .ppr-modal-footer {
    padding: var(--ew-s-4) var(--ew-s-5);
    border-top: 1px solid var(--ew-n-200);
    display: flex;
    justify-content: flex-end;
    gap: var(--ew-s-2);
  }

  .ppr-section {
    margin-bottom: var(--ew-s-5);
  }
  .ppr-section h3 {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/1 var(--ew-font);
    color: var(--ew-dark);
    margin-bottom: var(--ew-s-3);
    border-bottom: 1px solid var(--ew-n-200);
    padding-bottom: 6px;
  }

  .ppr-field-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-3);
  }
  .ppr-field { display: flex; flex-direction: column; gap: 4px; }
  .ppr-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase;
    letter-spacing: var(--ew-ls-caption);
    color: var(--ew-n-600);
  }
  .ppr-field label .req { color: #8F2414; }
  .ppr-field input, .ppr-field select, .ppr-field textarea {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    padding: 8px 10px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
  }
  .ppr-field textarea { min-height: 70px; resize: vertical; }

  /* Project picker */
  .ppr-picker {
    position: relative;
  }
  .ppr-picker-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid var(--ew-n-300);
    border-top: 0;
    border-radius: 0 0 var(--ew-r-md) var(--ew-r-md);
    max-height: 240px;
    overflow-y: auto;
    z-index: 10;
  }
  .ppr-picker-row {
    padding: 8px 10px;
    cursor: pointer;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .ppr-picker-row:hover { background: var(--ew-n-50); }
  .ppr-picker-row small { color: var(--ew-n-500); }

  /* Contacts table */
  .ppr-contacts {
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    overflow: hidden;
  }
  .ppr-contact-row {
    display: grid;
    grid-template-columns: 180px 1fr 1fr;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid var(--ew-n-100);
    align-items: center;
  }
  .ppr-contact-row:last-child { border-bottom: 0; }
  .ppr-contact-row.header {
    background: var(--ew-n-50);
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase;
    letter-spacing: var(--ew-ls-caption);
    color: var(--ew-n-600);
  }
  .ppr-contact-row input {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    padding: 6px 8px;
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-sm);
  }

  .ppr-error {
    color: #8F2414;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    margin-top: var(--ew-s-2);
  }
`;

interface ProjectListItem {
  id: number;
  jobNumber: string;
  title: string;
}

interface ContactRow {
  position: PprPosition;
  contactName: string;
  contactEmail: string;
  userId: string | null;
}

const EMPTY_CONTACTS: ContactRow[] = PPR_POSITIONS.map((position) => ({
  position,
  contactName: '',
  contactEmail: '',
  userId: null,
}));

export default function StartPprModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  // Project picker state
  const [projectSearch, setProjectSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // Form state
  const [jobNumber, setJobNumber] = useState('');
  const [jobName, setJobName] = useState('');
  const [jobValue, setJobValue] = useState<string>('');
  const [reviewType, setReviewType] = useState<PprReviewType>('Financial Only');
  const [reviewTypeOverridden, setReviewTypeOverridden] = useState(false);
  const [closeDate, setCloseDate] = useState('');
  const [presidentConsulted, setPresidentConsulted] = useState(false);
  const [presidentNotes, setPresidentNotes] = useState('');
  const [folderUrl, setFolderUrl] = useState('');
  const [contacts, setContacts] = useState<ContactRow[]>(EMPTY_CONTACTS);
  const [error, setError] = useState<string | null>(null);

  // Auto-update review type when jobValue changes, unless user has overridden.
  useEffect(() => {
    const num = parseFloat(jobValue);
    if (!isNaN(num) && !reviewTypeOverridden) {
      setReviewType(defaultReviewType(num));
    }
  }, [jobValue, reviewTypeOverridden]);

  // Search projects for the picker. Uses the PPR-specific unscoped search
  // (starting a PPR is cross-project; the normal Projects list is scoped to
  // the user's own assignments and wouldn't find an arbitrary job).
  const projectsQuery = useQuery({
    queryKey: ['ppr', 'project-search', projectSearch],
    queryFn: () => pprApi.searchProjects(projectSearch),
    enabled: showPicker && projectSearch.length > 0,
  });
  const projectMatches: ProjectListItem[] = useMemo(
    () => (Array.isArray(projectsQuery.data) ? projectsQuery.data.slice(0, 10) : []),
    [projectsQuery.data],
  );

  async function pickProject(p: ProjectListItem) {
    setShowPicker(false);
    setProjectSearch(`${p.jobNumber} — ${p.title}`);
    setSelectedProjectId(p.id);

    // Fetch pre-fill from API
    try {
      const pre = await pprApi.getProjectPrefill(p.id);
      setJobNumber(pre.jobNumber);
      setJobName(pre.jobName);
      if (pre.closeDate) setCloseDate(pre.closeDate.slice(0, 10));
      if (pre.sharepointFolderUrl) setFolderUrl(pre.sharepointFolderUrl);
      // Merge pre-filled contacts with the empty 9-row layout (preserving order)
      setContacts(
        PPR_POSITIONS.map((pos) => {
          const match = pre.contacts.find((c) => c.position === pos);
          return {
            position: pos,
            contactName: match?.contactName ?? '',
            contactEmail: match?.contactEmail ?? '',
            userId: match?.userId ?? null,
          };
        }),
      );
    } catch (err) {
      setError(`Failed to pre-fill from project: ${(err as Error).message}`);
    }
  }

  function updateContact(idx: number, field: 'contactName' | 'contactEmail', value: string) {
    setContacts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // If the user manually edits a name/email, drop the userId link
      // (so we don't claim a Microsoft user that may no longer apply).
      if (field === 'contactName' && next[idx].userId) {
        next[idx].userId = null;
      }
      return next;
    });
  }

  const createMut = useMutation({
    mutationFn: pprApi.createJob,
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ['ppr'] });
      navigate(`/ppr/${job.id}`);
    },
    onError: (err) => setError(`Save failed: ${(err as Error).message}`),
  });

  function handleSave() {
    setError(null);

    // Required fields
    const valueNum = parseFloat(jobValue);
    if (!jobNumber.trim()) return setError('Job Number is required');
    if (!jobName.trim()) return setError('Job Name is required');
    if (isNaN(valueNum) || valueNum < 0) return setError('Job Value must be a non-negative number');

    createMut.mutate({
      projectId: selectedProjectId ?? undefined,
      jobNumber: jobNumber.trim(),
      jobName: jobName.trim(),
      jobValue: valueNum,
      reviewType,
      closeDate: closeDate || undefined,
      presidentConsulted,
      presidentNotes: presidentNotes || undefined,
      sharepointFolderUrl: folderUrl.trim() || undefined,
      contacts: contacts.map((c) => ({
        position: c.position,
        contactName: c.contactName.trim() || undefined,
        contactEmail: c.contactEmail.trim() || undefined,
        userId: c.userId ?? undefined,
      })),
    });
  }

  return (
    <>
      <style>{css}</style>
      <div className="ppr-modal-backdrop" onClick={onClose}>
        <div className="ppr-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
          <div className="ppr-modal-header">
            <h2>Start Post Project Review</h2>
            <button className="ppr-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>

          <div className="ppr-modal-body">

            {/* Project picker */}
            <div className="ppr-section">
              <h3>Project</h3>
              <div className="ppr-field">
                <label>Select an existing project (optional — pre-fills name, number, people)</label>
                <div className="ppr-picker">
                  <input
                    type="search"
                    value={projectSearch}
                    placeholder="Type job number or name to search…"
                    onFocus={() => setShowPicker(true)}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setShowPicker(true);
                      // Clear the linked project if the user edits the field
                      if (selectedProjectId !== null) setSelectedProjectId(null);
                    }}
                    onBlur={() => setTimeout(() => setShowPicker(false), 150)}
                  />
                  {showPicker && projectMatches.length > 0 && (
                    <div className="ppr-picker-results">
                      {projectMatches.map((p) => (
                        <div
                          key={p.id}
                          className="ppr-picker-row"
                          onMouseDown={(e) => { e.preventDefault(); pickProject(p); }}
                        >
                          <strong>#{p.jobNumber}</strong> — {p.title}
                          <br />
                          <small>id {p.id}</small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job basics */}
            <div className="ppr-section">
              <h3>Job Details</h3>
              <div className="ppr-field-row">
                <div className="ppr-field">
                  <label>Job Number <span className="req">*</span></label>
                  <input value={jobNumber} onChange={(e) => setJobNumber(e.target.value)} />
                </div>
                <div className="ppr-field">
                  <label>Job Name <span className="req">*</span></label>
                  <input value={jobName} onChange={(e) => setJobName(e.target.value)} />
                </div>
              </div>
              <div className="ppr-field-row">
                <div className="ppr-field">
                  <label>Job Value (USD) <span className="req">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={jobValue}
                    onChange={(e) => setJobValue(e.target.value)}
                  />
                </div>
                <div className="ppr-field">
                  <label>Review Type</label>
                  <select
                    value={reviewType}
                    onChange={(e) => {
                      setReviewType(e.target.value as PprReviewType);
                      setReviewTypeOverridden(true);
                    }}
                  >
                    <option value="Financial Only">Financial Only</option>
                    <option value="Full Review">Full Review</option>
                  </select>
                </div>
                <div className="ppr-field">
                  <label>Close Date</label>
                  <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
                </div>
              </div>

              {/* President consult — only relevant for Financial Only on <$250K */}
              {reviewType === 'Financial Only' && (
                <div className="ppr-field-row">
                  <div className="ppr-field" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'inline-flex', gap: 6, textTransform: 'none', letterSpacing: 0 }}>
                      <input
                        type="checkbox"
                        checked={presidentConsulted}
                        onChange={(e) => setPresidentConsulted(e.target.checked)}
                      />
                      President consulted (extraordinary event on a low-value job)
                    </label>
                  </div>
                  {presidentConsulted && (
                    <div className="ppr-field" style={{ gridColumn: '1 / -1' }}>
                      <label>President Notes</label>
                      <textarea
                        value={presidentNotes}
                        onChange={(e) => setPresidentNotes(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="ppr-field-row">
                <div className="ppr-field" style={{ gridColumn: '1 / -1' }}>
                  <label>SharePoint Folder URL</label>
                  <input
                    type="url"
                    placeholder="https://eastandwestbrook.sharepoint.com/…"
                    value={folderUrl}
                    onChange={(e) => setFolderUrl(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Contacts */}
            <div className="ppr-section">
              <h3>Contacts (9 positions)</h3>
              <p style={{ fontSize: 'var(--ew-fs-caption)', color: 'var(--ew-n-600)', marginBottom: 'var(--ew-s-3)' }}>
                Name and email are optional but recommended. You can add or correct them later.
              </p>
              <div className="ppr-contacts">
                <div className="ppr-contact-row header">
                  <span>Position</span>
                  <span>Name</span>
                  <span>Email</span>
                </div>
                {contacts.map((c, i) => (
                  <div key={c.position} className="ppr-contact-row">
                    <span>{c.position}</span>
                    <input
                      value={c.contactName}
                      onChange={(e) => updateContact(i, 'contactName', e.target.value)}
                      placeholder="Name"
                    />
                    <input
                      type="email"
                      value={c.contactEmail}
                      onChange={(e) => updateContact(i, 'contactEmail', e.target.value)}
                      placeholder="email@eastandwestbrook.com"
                    />
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="ppr-error">{error}</div>}
          </div>

          <div className="ppr-modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={createMut.isPending}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Saving…' : 'Start PPR'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
